import { Button, Checkbox, Position as TTPosition, Spinner, Tooltip } from '@blueprintjs/core';
import { LIST } from '@blueprintjs/icons/lib/esm/generated/iconNames';
import styled from '@emotion/styled';
import { ICellRendererParams } from 'ag-grid-community';
import { inject, observer } from 'mobx-react';
import { Instance } from 'mobx-state-tree';
import * as React from 'react';
import { Circle, GoogleMap } from '@react-google-maps/api';
import { TLCode } from '../../common/enums';
import { generateCustomIcon } from '../../common/icons';
import {
  AnchorStore,
  GroupStore,
  PharmacyDetailedStore,
  PharmacyStore,
  UiStore,
  SecretariatNeighborStore,
  AuthenticationStore,
} from '../../store';
import { Group } from '../../store/models/Group';
import { PharmacyDetails } from '../../store/models/PharmacyDetails';
import { getUserLocation } from '../../store/utils';
import GroupSelection from '../common/dropdownSelection/GroupSelection';
import BaseGridXL from '../common/grid/BaseGridXL';
import DeleteCell from '../common/grid/DeleteCell';
import PharmagardeMap from '../common/map/PharmagardeMap';
import PharmacyMarker from '../common/PharmacyMarker';
import { TranslatedMessage } from '../common/translation';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';

interface PharmaciesMapProps extends WithTranslateProps {
  isLoading: boolean;
  onBackToList: () => void;
  pharmaciesList: Array<Instance<typeof PharmacyDetails>>;
  AnchorStore?: Instance<typeof AnchorStore>;
  UiStore?: Instance<typeof UiStore>;
  GroupStore?: Instance<typeof GroupStore>;
  PharmacyStore?: Instance<typeof PharmacyStore>;
  PharmacyDetailedStore?: Instance<typeof PharmacyDetailedStore>;
  SecretariatNeighborStore?: Instance<typeof SecretariatNeighborStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  groupList?: Array<Instance<typeof Group>>;
}

interface PharmaciesMapState {
  userLocation?: GeolocationPosition;
  initialZoomCalculated: boolean;
  group: string;
  groups: any[];
  grpColors: any;
  level: number;
  showLevel0: number;
  showLevel0Nearby: number;
  showLevel1: number;
  showOthers: boolean;
  showAnchors: boolean;
  showInactive: boolean;
  groupCheckboxesDisabled: boolean;
  selectedGroup?: string;
  selectedGroupId?: number;
  groupPharmacyList: any[]; // Array<Instance<typeof PharmacyDetails>>;
  newGroupMemberList: any[]; // Array<Instance<typeof PharmacyDetails>>;
  removedGroupMemberList: any[]; // Array<Instance<typeof PharmacyDetails>>;
}

@inject(
  'AuthenticationStore',
  'AnchorStore',
  'UiStore',
  'GroupStore',
  'PharmacyStore',
  'PharmacyDetailedStore',
  'SecretariatNeighborStore',
)
@observer
class PharmaciesMap extends React.Component<PharmaciesMapProps, PharmaciesMapState> {
  state = {
    initialZoomCalculated: false,
    group: '*',
    groups: [],
    level: -1,
    showLevel0: -1,
    showLevel0Nearby: 0,
    showLevel1: -1,
    showOthers: false,
    showAnchors: false,
    showInactive: false,
    grpColors: {
      0: 'FFCA18',
      1: 'FFCA18',
      A: 'ea735f',
      B: '6a93fd',
      C: '67dddd',
      D: '28e260',
      E: 'db9a5e',
      F: 'dbd75e',
      G: '9cdb5e',
      H: '4bc6ba', // '5edbd0',
      I: '047ace', //'0a2696',
      J: '2e6c84',
      K: '2b7f0a',
      L: '16b746',
    },
    groupCheckboxesDisabled: false,
    groupPharmacyList: [],
    newGroupMemberList: [],
    removedGroupMemberList: [],
  } as PharmaciesMapState;

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    const { AnchorStore, GroupStore, SecretariatNeighborStore } = this.props;
    try {
      const location = await getUserLocation();
      this._isMounted && this.setState({ userLocation: location });
    } catch (err) {
      console.error(`Error while getting user location: ${(err as any).message}`);
    }
    await AnchorStore!.loadList();
    await GroupStore!.loadList();
    await SecretariatNeighborStore!.loadList();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  findCenterCoordinate = () => {
    const { pharmaciesList } = this.props;
    let top;
    let bottom;
    let left;
    let right;
    if (pharmaciesList.length === 0) return undefined;
    pharmaciesList
      .filter((ph) => ph.latitude && ph.longitude)
      .map((ph) => {
        return { latitude: ph.latitude!, longitude: ph.longitude! };
      })
      .forEach((point) => {
        if (!top || point.latitude > top) {
          top = point.latitude;
        }
        if (!bottom || point.latitude < bottom) {
          bottom = point.latitude;
        }
        if (!left || point.longitude < left) {
          left = point.longitude;
        }
        if (!right || point.longitude > right) {
          right = point.longitude;
        }
      });
    const lat = (top + bottom) / 2;
    const lng = (left + right) / 2;

    return {
      lat,
      lng,
    };
  };

  renderPharmacyMarkers = () => {
    const { pharmaciesList, groupList } = this.props;
    const {
      group,
      groups,
      groupCheckboxesDisabled,
      level,
      grpColors,
      showLevel0,
      showOthers,
      showLevel0Nearby,
      showLevel1,
    } = this.state;
    const secretariatId = this.props.AuthenticationStore!.secretariatId
      ? this.props.AuthenticationStore!.secretariatId
      : 1;

    return pharmaciesList
      .filter(
        (ph) =>
          ph.latitude &&
          ph.longitude &&
          ph.isActive &&
          !groupCheckboxesDisabled &&
          (groups.filter(
            (grp) =>
              grp.value === ph.group! ||
              //  || (grp.value = 'nearby' && this.isNearby(ph))
              (grp.value === '' && ph.group === undefined),
          ).length > 0 ||
            (showLevel0 == ph.level ||
              (showLevel1 == ph.level && ph.secretariatId === secretariatId) ||
              (showLevel0Nearby && this.isNearby(ph)) ||
              (showOthers && ph.level > 0 && ph.secretariatId != secretariatId))),
      )
      .map((ph) => {
        const isLvl0 = ph.level === 0;
        const size = 20; //isLvl0?20:32
        const grp = groupList!.filter((grp) => grp.name === ph.group)[0]; // grpColors[ph.group!]
        let upColor = grp ? grp.color : 'FFCA18';
        upColor = upColor == undefined ? 'FFCA18' : upColor;
        let bottomColor = upColor; // '888888'
        let icon = generateCustomIcon(
          size,
          size,
          upColor,
          bottomColor,
          ph.level === 3,
          '000000',
          isLvl0,
          '888888',
          '1',
          '1',
        );
        return (
          <PharmacyMarker
            key={ph.pharmacyId}
            id={ph.apbNumber}
            marker={ph}
            icon={icon}
            onMarkForGroupChangeClick={this.onMarkForGroupChange}
            groupEditMode
          />
        );
      });
  };

  renderNewMemberMarkers = () => {
    const { groups, groupCheckboxesDisabled, showLevel0, showLevel1, newGroupMemberList } = this.state;

    return newGroupMemberList
      .filter(
        (ph) =>
          ph.latitude &&
          ph.longitude &&
          !groupCheckboxesDisabled &&
          (groups.filter((grp) => grp.value === ph.group! || (grp.value === '' && ph.group === undefined)).length > 0 ||
            // && ((groups.filter(grp => grp.value === '*' || grp.value === ph.group!).length > 0)
            (showLevel0 == ph.level || showLevel1 == ph.level)),
      )
      .map((ph) => {
        const isLvl0 = ph.level === 0;
        const size = 20; //isLvl0?20:32
        let upColor = 'FFFFFF';
        let bottomColor = upColor; // '888888'
        let icon = generateCustomIcon(
          size,
          size,
          upColor,
          bottomColor,
          ph.level === 3,
          '000000',
          isLvl0,
          '888888',
          '1',
          '1',
        );
        return (
          <PharmacyMarker
            key={ph.pharmacyId}
            id={ph.apbNumber}
            marker={ph}
            icon={icon}
            onMarkForGroupChangeClick={this.onMarkForGroupChange}
            groupEditMode
          />
        );
      });
  };

  renderRemovedMemberMarkers = () => {
    const {
      group,
      groups,
      groupCheckboxesDisabled,
      level,
      grpColors,
      showLevel0,
      showLevel1,
      removedGroupMemberList,
    } = this.state;

    return removedGroupMemberList
      .filter((ph) => ph.latitude && ph.longitude)
      .map((ph) => {
        const isLvl0 = ph.level === 0;
        const size = 20; //isLvl0?20:32
        let upColor = '123456';
        let bottomColor = upColor; // '888888'
        let icon = generateCustomIcon(
          size,
          size,
          upColor,
          bottomColor,
          ph.level === 3,
          '000000',
          isLvl0,
          '888888',
          '1',
          '1',
        );
        return (
          <PharmacyMarker
            key={ph.pharmacyId}
            id={ph.apbNumber}
            marker={ph}
            icon={icon}
            onMarkForGroupChangeClick={this.onMarkForGroupChange}
            groupEditMode
          />
        );
      });
  };

  renderAnchorMarkers = () => {
    const markers: any[] = [];
    if (this.state.showAnchors) {
      this.props.AnchorStore!.dataList.map((elem) => {
        markers.push(
          <Circle
            key={elem.anchorId + 'Circle'}
            center={{ lat: Number(elem.latitude), lng: Number(elem.longitude) }}
            radius={elem.radius}
            options={{
              fillColor: '#10a259',
              strokeWeight: 0.5,
            }}
          />,
        );
      });
    }
    return markers;
  };

  renderInactiveMarkers = () => {
    const { pharmaciesList, groupList } = this.props;
    const { group, groups, groupCheckboxesDisabled, level, grpColors, showLevel0, showLevel1 } = this.state;
    const markers: any[] = [];
    if (this.state.showInactive) {
      pharmaciesList
        .filter((ph) => ph.latitude && ph.longitude && !ph.isActive)
        .map((ph) => {
          const isLvl0 = ph.level === 0;
          const size = 20; //isLvl0?20:32
          let upColor = 'FF0000';
          let bottomColor = upColor;
          let icon = generateCustomIcon(
            size,
            size,
            upColor,
            bottomColor,
            ph.level === 3,
            '000000',
            isLvl0,
            '888888',
            '1',
            '1',
          );
          markers.push(
            <PharmacyMarker
              key={ph.pharmacyId}
              id={ph.apbNumber}
              marker={ph}
              icon={icon}
              onMarkForGroupChangeClick={this.onMarkForGroupChange}
              groupEditMode
            />,
          );
        });
    }
    return markers;
  };

  isNearby = (ph) => {
    const secretariatId = this.props.AuthenticationStore!.secretariatId
      ? this.props.AuthenticationStore!.secretariatId
      : 1;
    var result =
      ph.level == 0 &&
      this.props.SecretariatNeighborStore!.dataList.find(
        (item) => item.pharmacyId == ph.pharmacyId && item.secretariatId == secretariatId,
      );
    return result;
  };

  handleShowAnchorChange = (event) => {
    this.setState({ showAnchors: !this.state.showAnchors });
  };

  handleShowInactiveChange = (event) => {
    this.setState({ showInactive: !this.state.showInactive });
  };

  handleShowLevelChange = (event) => {
    console.log(event);
    if (event.target.id === 'level0') {
      if (event.target.checked) {
        this.setState({ showLevel0: 0 });
      } else {
        this.setState({ showLevel0: -1 });
      }
    }
    if (event.target.id === 'level0Nearby') {
      if (event.target.checked) {
        this.setState({ showLevel0Nearby: 1 });
      } else {
        this.setState({ showLevel0Nearby: 0 });
      }
    }
    if (event.target.id === 'others') {
      if (event.target.checked) {
        this.setState({ showOthers: true });
      } else {
        this.setState({ showOthers: false });
      }
    }
    if (event.currentTarget.id === 'level1') {
      if (event.currentTarget.checked) {
        this.setState({ showLevel1: 1 });
      } else {
        this.setState({ showLevel1: -1 });
      }
    }
  };

  handleLevelSelect = (event) => {
    this.setState({ level: event.value });
  };

  handleGroupSelect = (event) => {
    this.setState({ groups: event });
  };

  handleGroupToggle = (grp) => {
    let newgroups;
    if (this.state.groups.filter((group) => group.value == grp).length > 0) {
      newgroups = this.state.groups.filter((group) => group.value != grp);
    } else {
      newgroups = this.state.groups;
      newgroups.push({ label: grp, value: grp });
    }
    this.setState({ groups: newgroups });
  };

  onGroupSelected = async (value: Instance<typeof Group>) => {
    const res = this.props.PharmacyStore!.dataList.filter((ph) => ph.group === value.name);
    this._isMounted && this.setState({ groupPharmacyList: res, selectedGroup: value.name });
    let newgroups = this.state.groups;
    newgroups.push({ label: value.name, value: value.name });
    this._isMounted && this.setState({ groups: newgroups });
  };

  onMarkForGroupChange = (pharmacyId) => {
    const res = this.props.PharmacyStore!.dataList.filter((ph) => ph.pharmacyId === pharmacyId);
    this.setState({ newGroupMemberList: this.state.newGroupMemberList.concat(res[0]) });
  };

  async setGroup(groupName: string) {
    this.loadGroupPharmacyList(groupName);
  }

  loadGroupPharmacyList = async (groupName) => {
    const res = this.props.PharmacyStore!.dataList.filter((ph) => ph.group === groupName);
    this._isMounted && this.setState({ groupPharmacyList: res });
  };

  private columnDefs = [
    {
      headerName: 'L',
      field: 'level',
      width: 40,
      filter: false,
      sortable: true,
    },
    {
      headerName: 'G',
      field: 'group',
      width: 45,
      filter: false,
      sortable: true,
      resizable: false,
    },
    {
      headerName: this.props.translate(TLCode.PHARMACIES_ApbNum),
      field: 'apbNumber',
      width: 85,
      resizable: false,
    },
    {
      headerName: this.props.translate(TLCode.PHARMACIES_PharmacyName),
      field: 'name',
      resizable: true,
      cellRenderer: (params: ICellRendererParams) => {
        return params.data.name ? params.data.name.replace('Pharmacie ', '') : '';
      },
    },
    {
      headerName: '',
      width: 50,
      resizable: false,
      cellRendererFramework: DeleteCell,
      cellRendererParams: (params: ICellRendererParams) => {
        return {
          onDeleteButtonClick: async () => {
            this.onRemoveFromGroup(params);
          },
        };
      },
    },
  ];

  private columnDefs2 = [
    {
      headerName: 'L', //this.props.translate(TLCode.PHARMACIES_Level),
      field: 'level',
      width: 45,
      filter: false,
      sortable: false,
      resizable: false,
    },
    {
      headerName: 'G', //this.props.translate(TLCode.PHARMACIES_Group),
      field: 'group',
      width: 45,
      filter: false,
      sortable: false,
      resizable: false,
    },
    {
      headerName: this.props.translate(TLCode.PHARMACIES_ApbNum),
      field: 'apbNumber',
      width: 90,
      filter: false,
      sortable: false,
      resizable: false,
    },
    {
      headerName: this.props.translate(TLCode.PHARMACIES_PharmacyName),
      field: 'name',
      resizable: true,
      cellRenderer: (params: ICellRendererParams) => {
        return params.data.name.replace('Pharmacie ', '');
      },
    },
    {
      headerName: '',
      width: 50,
      resizable: false,
      cellRendererFramework: DeleteCell,
      cellRendererParams: (params: ICellRendererParams) => {
        return {
          onDeleteButtonClick: async () => {
            this.onRemove(params);
          },
        };
      },
    },
  ];

  buildGridData = () => {
    return this.state.groupPharmacyList.map((p) => ({
      pharmacyId: p.pharmacyId,
      apbNumber: p.apbNumber,
      name: p.name,
      group: p.group,
      level: p.level,
    }));
  };

  buildNewGroupMemberGridData = () => {
    return this.state.newGroupMemberList.map((p) => ({
      pharmacyId: p.pharmacyId,
      apbNumber: p.apbNumber,
      name: p.name,
      group: p.group,
      level: p.level,
    }));
  };

  onRemove = (params: ICellRendererParams) => {
    const lst = this.state.newGroupMemberList.filter((ph) => {
      return ph.pharmacyId !== params.data.pharmacyId;
    });
    var selectedData = params.api.getSelectedRows();
    var res = params.api.setRowData(lst);
    this.setState({ newGroupMemberList: lst });
  };

  onRemoveFromGroup = (params: ICellRendererParams) => {
    let lst = this.state.removedGroupMemberList;
    const ph = this.state.groupPharmacyList.find((ph) => {
      return ph.pharmacyId === params.data.pharmacyId;
    });
    lst.push(ph);
    this.setState({ removedGroupMemberList: lst });
  };

  onRegisterMembers = async () => {
    if (this.state.selectedGroup) {
      await this.props.PharmacyDetailedStore!.registerMembers(this.state.newGroupMemberList, this.state.selectedGroup!);
      this._isMounted && this.setState({ newGroupMemberList: [] });
      await this.props.PharmacyDetailedStore!.unregisterMembers(this.state.removedGroupMemberList);
      this._isMounted && this.setState({ removedGroupMemberList: [] });
      await this.props.PharmacyStore!.loadList();
      this._isMounted && this.setState({ selectedGroup: '', groupPharmacyList: [] });
    }
  };

  render() {
    const { userLocation, initialZoomCalculated, group, grpColors, selectedGroupId } = this.state;
    const { isLoading, translate, onBackToList, groupList, GroupStore } = this.props;
    const centerCoordinate = this.findCenterCoordinate();
    const centerCoordinates = this.props.UiStore!.centerCoordinates!;
    console.log('RENDERINGMAP_' + centerCoordinate!.lat + '-' + centerCoordinate!.lng + ' DATE=' + new Date());

    return (
      <Container>
        {isLoading ? (
          <Spinner />
        ) : (
          <MapContainer>
            <DivLine>
              <DivColumn>
                <Checkbox checked={this.state.showAnchors} onChange={this.handleShowAnchorChange}>
                  Ancrages
                </Checkbox>
              </DivColumn>
              <DivColumn>
                <Checkbox checked={this.state.showInactive} onChange={this.handleShowInactiveChange}>
                  Pharmacies inactives
                </Checkbox>
              </DivColumn>
              <Button icon={LIST} onClick={onBackToList} text={translate(TLCode.PHARMACIES_BackToList)} />
            </DivLine>
            <DivLine>
              <DivColumn1 style={{ width: 90 }}>
                <div style={{ background: '#' + grpColors[0] }}>
                  <Checkbox
                    disabled={this.state.groupCheckboxesDisabled}
                    id="level0"
                    checked={this.state.showLevel0 === 0}
                    onChange={this.handleShowLevelChange}
                    style={{ marginLeft: '5px', marginRight: '10px' }}
                  >
                    Niv.0
                  </Checkbox>
                </div>
                <div style={{ background: '#FFCA18' }}>
                  <Checkbox
                    disabled={this.state.groupCheckboxesDisabled}
                    id="others"
                    checked={this.state.showOthers}
                    onChange={this.handleShowLevelChange}
                    style={{ marginLeft: '5px', marginRight: '10px' }}
                  >
                    Autres
                  </Checkbox>
                </div>
                <div style={{ background: '#878080' }}>
                  <Checkbox
                    disabled={this.state.groupCheckboxesDisabled}
                    id="level0Nearby"
                    checked={this.state.showLevel0Nearby > 0}
                    onChange={this.handleShowLevelChange}
                    style={{ marginLeft: '5px', marginRight: '10px' }}
                  >
                    Proches
                  </Checkbox>
                </div>
                <div style={{ background: '#' + grpColors[1] }}>
                  <Checkbox
                    disabled={this.state.groupCheckboxesDisabled}
                    id="level1"
                    checked={this.state.showLevel1 === 1}
                    onChange={this.handleShowLevelChange}
                    style={{ marginLeft: '5px', marginRight: '10px' }}
                  >
                    Niv.1
                  </Checkbox>
                </div>
                {this.props.groupList &&
                  this.props.groupList!.map((grp) => (
                    <div style={{ background: '#' + grp.color }} key={grp.description}>
                      <Tooltip content={grp.description} position={TTPosition.LEFT} hoverOpenDelay={800}>
                        <Checkbox
                          disabled={this.state.groupCheckboxesDisabled}
                          onChange={() => this.handleGroupToggle(grp.name)}
                          checked={this.state.groups.filter((group) => group.value == grp.name).length > 0}
                          style={{ marginLeft: '5px', marginRight: '10px' }}
                        >
                          {grp.name}
                        </Checkbox>
                      </Tooltip>
                    </div>
                  ))}
                <div style={{ margin: '0 auto' }}>
                  <Button
                    intent="primary"
                    small={true}
                    style={{ height: '30px' }}
                    onClick={() => {
                      this.setState({ groupCheckboxesDisabled: !this.state.groupCheckboxesDisabled });
                    }}
                    text={'on/off'}
                  />
                </div>
                <br />
                <br />
                <div style={{ margin: '0 auto' }}>
                  <Button
                    intent="primary"
                    small={true}
                    style={{ height: '30px' }}
                    onClick={() => {
                      let lst = Array<any>();
                      this.props.groupList!.forEach((grp) => lst.push({ label: grp.name, value: grp.name }));
                      this.setState({ groups: lst });
                      this.setState({ showLevel0: 0, showLevel0Nearby: 1, showLevel1: 1 });
                    }}
                    text={translate(TLCode.DUTIES_CheckAllGroups)}
                  />
                </div>
                <div style={{ margin: '0 auto' }}>
                  <Button
                    intent="primary"
                    small={true}
                    style={{ height: '30px' }}
                    onClick={() => {
                      this.setState({ groups: [] });
                      this.setState({ showLevel0: -1, showLevel0Nearby: 0, showLevel1: -1 });
                    }}
                    text={translate(TLCode.DUTIES_UncheckAllGroups)}
                  />
                </div>
              </DivColumn1>
              <DivColumn2>
                <PharmagardeMap
                  center={
                    centerCoordinates
                      ? centerCoordinates
                      : userLocation
                      ? {
                          lat: userLocation.coords.latitude,
                          lng: userLocation.coords.longitude,
                        }
                      : undefined
                  }
                  onTilesLoaded={() => {
                    this._isMounted && this.setState({ initialZoomCalculated: true });
                  }}
                  gridSize={5}
                  maximalZoom={18}
                  minimalZoom={8}
                  initialZoom={11}
                >
                  {initialZoomCalculated
                    ? [
                        ...this.renderPharmacyMarkers(),
                        ...this.renderNewMemberMarkers(),
                        ...this.renderRemovedMemberMarkers(),
                        ...this.renderAnchorMarkers(),
                        ...this.renderInactiveMarkers(),
                      ]
                    : undefined}
                </PharmagardeMap>
              </DivColumn2>
              <DivColumn style={{ width: 325, paddingLeft: 3 }}>
                <DivLine>
                  <DivColumn style={{ paddingLeft: 3, paddingTop: 5, paddingBottom: 5, width: 125 }}>
                    <b>
                      <TranslatedMessage id={TLCode.GROUPS_Target} />
                    </b>
                  </DivColumn>
                  <DivColumn style={{ paddingTop: 5, paddingBottom: 5, width: 300 }}>
                    <GroupSelection
                      style={{ paddingLeft: 10 }}
                      groupList={GroupStore!.dataList}
                      selectedGroupId={selectedGroupId}
                      getGroupById={GroupStore!.getById}
                      onChange={this.onGroupSelected}
                    />
                  </DivColumn>
                </DivLine>
                <BaseGridXL keyName="pharmacyId" columnDefs={this.columnDefs} rowData={this.buildGridData()} />
                <DivColumn style={{ paddingLeft: 3, paddingTop: 5, paddingBottom: 5 }}>
                  <b>
                    <TranslatedMessage id={TLCode.GROUPS_NewMembers} />
                  </b>
                </DivColumn>
                <BaseGridXL
                  keyName="pharmacyId"
                  columnDefs={this.columnDefs2}
                  rowData={this.buildNewGroupMemberGridData()}
                />
                <DivColumn style={{ paddingTop: 3 }}>
                  <Button
                    style={{ width: 100, alignSelf: 'center' }}
                    intent="primary"
                    onClick={this.onRegisterMembers}
                    text={translate(TLCode.GROUP_RegisterMembers)}
                  />
                </DivColumn>
              </DivColumn>
            </DivLine>
          </MapContainer>
        )}
      </Container>
    );
  }
}

export default withTranslate(PharmaciesMap);
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const MapContainer = styled.div`
  flex: 1;
  width: 100%;
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const DivColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 300px;
`;

const DivColumn1 = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 60px;
`;

const DivColumn2 = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 1550px;
  height: 800px;
`;
